import {isPopulatedArray, isPopulatedString, isString} from './common/typing.helpers';
import {NumericRange} from './common/common.types';

export function omitPropertiesByKeyFrom<ObjectType extends object, Keys extends (keyof ObjectType)[]>(
  object: ObjectType, ...propertyKeysToOmit: Keys
): Omit<ObjectType, typeof propertyKeysToOmit[number]> {
  // https://stackoverflow.com/a/65392602 was helpful when implementing this.  The typing wasn't quite right there, but it was close enough to adapt to this.

  return (Object.keys(object) as (keyof ObjectType)[]).reduce(
    (accumulator, key) => ({
      ...accumulator,
      ...!propertyKeysToOmit.includes(key) && {[key]: object[key]}
    }),
    {} as ObjectType
  );
}

export function extractPropertiesByKeyFrom<T extends object, K extends keyof T>(object: T, ...propertyKeysToExtract: K[]): Pick<T, K> {
  return Object.entries(object).reduce(
    (accumulator, [key, value]) => ({
      ...accumulator,
      ...propertyKeysToExtract.includes(key as K) && {[key]: value}
    }),
    {} as Pick<T, K>
  );
}

/** Useful for auto-generating a record from an array of keys, as long as all values should be the same. */
export function generateRecordForKeys<const K extends readonly PropertyKey[], V>(keys: K, valueForEachKey: V): Record<K[number], V> {
  return Object.fromEntries(keys.map(key => [key, valueForEachKey])) as Record<K[number], V>;
}

export function capitalize(string: string): string {
  if (!isString(string)) return '';

  return string.replace(/^\w/, firstCharacter => firstCharacter.toUpperCase());
}

export function toTitleCase(string: string): string {
  if (!isString(string)) return '';
  const words = string.split(' ');
  return words.map((word) => {
    return word[0].toUpperCase() + word.substring(1).toLowerCase();
  }).join(' ');
}

export const lowerCamelCaseToUpperCamelCase = capitalize;

export function camelCaseToTitleCase(string: string): string {
  if (!isString(string)) return '';

  return capitalize(string.replace(/([A-Z])/g, ' $1'));
}

export function camelCaseToSentenceCase(string: string): string {
  return capitalize(camelCaseToLowerSentenceCase(string));
}

export function camelCaseToLowerSentenceCase(string: string): string {
  if (!isString(string)) return '';

  return string.replace(/([A-Z])/g, ' $1').toLowerCase();
}

export function kebabCaseToSentenceCase(string: string): string {
  if (!isString(string)) return '';

  return string.replace(/-/g, ' ');
}

export function enumCaseToTitleCase(string: string): string {
  if (!isString(string)) return '';
  const words = string.split('_');

  return words.map((word) => {
    return word[0].toUpperCase() + word.substring(1).toLowerCase();
  }).join(' ');
}

export function enumCaseToSentenceCase(string: string): string {
  if (!isString(string)) return '';

  return string
    .split('_')
    .map((word, index) =>
      index === 0
        ? word[0].toUpperCase() + word.substring(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
}

export function truncateTo(maximumCharacters: number, string: string, suffix: string = '...'): string {
  if (!isString(string)) return '';

  const trimmedString: string = string.trim();

  return trimmedString.length <= maximumCharacters ? trimmedString : `${trimmedString.slice(0, maximumCharacters)}${suffix}`;
}

export function joinStrings(...strings: string[]): string {
  return isPopulatedArray(strings) ? strings.filter(isPopulatedString).join(' ') : '';
}

/** Returns (for example) `[2, 3, 4, 5]` for `[2, 5]`.  Safely returns an empty array if the range's end value is less than its start value. */
export function numericRangeToArrayOfNumbers(range: NumericRange): number[] {
  const [start, end]: NumericRange = range;

  return Array.from({length: end - start + 1}, (_, index) => start + index);
}

export function deduplicate<T>(array: T[]): T[] {
  return [...new Set<T>(array)];
}

export function asSameTypeOrUndefined<T>(thing: T): T | undefined {
  return thing;
}
